import { StandardStatus } from '@zoocasa/go-search';
import { isActive, isSold, isPending, isExpired } from 'types/reso';

/**
 * ListingStatus Value Object
 * 
 * Domain concept: "What lifecycle state is this listing in?"
 * 
 * Business rules:
 * - Uses RESO standard status values
 * - Rental vs sale affects terminology (sold vs leased)
 * - Null status is valid (use hasValue to check)
 * 
 * Computed facts:
 * - hasValue: Whether status data exists (not null)
 * - isActive: Listing is on market (available)
 * - isSold: Transaction complete (sold or leased)
 * - isPending: Under contract
 * - isExpired: Listing ended without sale
 * 
 * Usage:
 * - Service: src/domain/listing/listing-badge-service.ts
 * - UI: src/ui/badges/status-badge.tsx
 * 
 * Note: RESO status helpers are imported from types/reso. If status
 * logic changes, update those helpers, not this VO.
 */
export class ListingStatus {
  readonly #status: StandardStatus | null;
  readonly #isRental: boolean;

  private constructor(status: StandardStatus | null, isRental: boolean) {
    this.#status = status;
    this.#isRental = isRental;
  }

  /**
   * Creates a ListingStatus from listing data.
   * Always returns an instance - use hasValue to check validity.
   */
  static from(status: StandardStatus | null, isRental: boolean): ListingStatus {
    return new ListingStatus(status, isRental);
  }

  /**
   * Whether we have valid status data.
   */
  get hasValue(): boolean {
    return this.#status !== null;
  }

  /**
   * Gets the raw RESO standard status value.
   */
  get value(): StandardStatus | null {
    return this.#status;
  }

  /**
   * Whether this is a rental listing.
   */
  get isRental(): boolean {
    return this.#isRental;
  }

  /**
   * Whether the listing is active (available for sale/rent).
   */
  get isActive(): boolean {
    if (!this.#status) return false;
    return isActive(this.#status);
  }

  /**
   * Whether the listing is sold/rented.
   */
  get isSold(): boolean {
    if (!this.#status) return false;
    return isSold(this.#status);
  }

  /**
   * Whether the listing is pending.
   */
  get isPending(): boolean {
    if (!this.#status) return false;
    return isPending(this.#status);
  }

  /**
   * Whether the listing is expired.
   */
  get isExpired(): boolean {
    if (!this.#status) return false;
    return isExpired(this.#status);
  }
}
